import { useMemo } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Radar, Compass, ArrowLeft } from "lucide-react";
import { useOpportunities } from "@/hooks/useOpportunities";
import { deriveDaysAgo } from "@/utils/opportunityDerived";

/**
 * Market Insights — an editorial "state of the market" block composed
 * purely from the loaded opportunity feed. No new API. Three cards:
 *   1. Fastest-growing industry (by count-in-last-week)
 *   2. Highest-scoring pending signal (urgent time-sensitivity)
 *   3. Most-covered country this week
 *
 * If the feed doesn't support one of the insights (empty group), that
 * card gracefully hides — the section always looks curated, never empty.
 */
export const MarketInsights = () => {
  const { opportunities } = useOpportunities();

  const insights = useMemo(() => {
    if (!opportunities.length) return [];

    // 1) Hottest industry by count
    const industryCounts = new Map();
    for (const o of opportunities) {
      industryCounts.set(o.industry, (industryCounts.get(o.industry) ?? 0) + 1);
    }
    const [topIndustry, topIndustryCount] =
      [...industryCounts.entries()].sort((a, b) => b[1] - a[1])[0] ?? [];

    // 2) Highest-scoring urgent
    const urgent = opportunities
      .filter((o) => (o.meta || {}).timeSensitivity === "urgent")
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0];

    // 3) Most-covered country
    const countryCounts = new Map();
    for (const o of opportunities) {
      countryCounts.set(o.country, (countryCounts.get(o.country) ?? 0) + 1);
    }
    const [topCountry, topCountryCount] =
      [...countryCounts.entries()].sort((a, b) => b[1] - a[1])[0] ?? [];

    // 4) Recent signals count (7-day window)
    const recent = opportunities.filter((o) => deriveDaysAgo(o.publishedAt) <= 7).length;

    const cards = [];
    if (topIndustry && topIndustryCount > 0) {
      cards.push({
        key: "sector",
        icon: TrendingUp,
        eyebrow: "قطاع صاعد",
        title: topIndustry,
        body: `${topIndustryCount} فرصة نشطة هذا الأسبوع — الأعلى بين القطاعات المُتابَعة.`,
        to: `/opportunities?industry=${encodeURIComponent(topIndustry)}`,
        cta: "استكشف القطاع",
      });
    }
    if (urgent) {
      cards.push({
        key: "urgent",
        icon: Radar,
        eyebrow: "إشارة عاجلة",
        title: urgent.title,
        body: urgent.summary,
        to: `/opportunities/${urgent.id}`,
        cta: "اقرأ الملف الكامل",
        accent: true,
      });
    }
    if (topCountry && topCountryCount > 0) {
      cards.push({
        key: "country",
        icon: Compass,
        eyebrow: "أعلى تغطية",
        title: topCountry,
        body: `${topCountryCount} فرصة من ${topCountry} · ${recent} إشارة جديدة على مستوى المنطقة هذا الأسبوع.`,
        to: `/opportunities?country=${encodeURIComponent(topCountry)}`,
        cta: "استكشف الدولة",
      });
    }
    return cards;
  }, [opportunities]);

  if (!insights.length) return null;

  return (
    <section
      id="insights"
      data-testid="market-insights"
      className="bg-surface-50"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="fursa-eyebrow">Market Insights</p>
            <h2 className="fursa-section-title mt-3 font-semibold tracking-tight text-ink-900">
              نظرة أسبوعية على السوق
            </h2>
            <p className="mt-3 text-base leading-relaxed text-ink-500">
              مؤشرات مُستخلَصة من الفرص النشطة — أين تتحرّك الأموال، وما القطاعات التي تسبّق غيرها.
            </p>
          </div>
          <div className="fursa-rule w-24" aria-hidden />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {insights.map((ins) => {
            const Icon = ins.icon;
            return (
              <Link
                key={ins.key}
                to={ins.to}
                data-testid={`market-insight-${ins.key}`}
                className={`group flex flex-col rounded-xl border bg-white p-6 shadow-card transition-all duration-300 ease-out-expo hover:-translate-y-1 hover:shadow-card-hover ${
                  ins.accent
                    ? "border-gold-200 hover:border-gold-400"
                    : "border-surface-200 hover:border-fursa-300"
                }`}
              >
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${
                  ins.accent ? "bg-gold-100 text-gold-700" : "bg-fursa-50 text-fursa-700"
                }`}>
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <p className={`mt-4 text-[10px] font-semibold uppercase tracking-widest ${
                  ins.accent ? "text-gold-700" : "text-fursa-700"
                }`}>
                  {ins.eyebrow}
                </p>
                <h3 className="mt-2 text-lg font-semibold leading-snug tracking-tight text-ink-900 line-clamp-2">
                  {ins.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-500 line-clamp-3">
                  {ins.body}
                </p>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-fursa-700 transition-colors group-hover:text-fursa-800">
                  {ins.cta}
                  <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" strokeWidth={1.75} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
